import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Bug as BugIcon, History, Lightbulb, Menu, Route, X } from 'lucide-react';
import type { Portal } from '../../services/platformApi';
import { PlatformPages, PlatformVersions } from './PlatformPages';
import { BugReportModal } from './BugReportModal';
import { usePlatformIdentity } from './usePlatformIdentity';
import './platform.css';

export function usePlatformNavigation() {
  const [params, setParams] = useSearchParams();
  return () => {
    if (!params.has('platformView') && !params.has('platformAdminTab') && !params.has('platformFeedbackId') && !params.has('platformBacklogId')) return;
    const next = new URLSearchParams(params);
    next.delete('platformView'); next.delete('platformAdminTab');
    next.delete('platformFeedbackId'); next.delete('platformBacklogId');
    setParams(next);
  };
}

export function PlatformMenu() {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    container.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const dismiss = (event: MouseEvent) => { if (!container.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('click', dismiss);
    return () => document.removeEventListener('click', dismiss);
  }, [open]);
  const navigate = (view: string) => {
    const next = new URLSearchParams(params);
    next.set('platformView', view);
    setParams(next);
    setOpen(false);
    trigger.current?.focus();
  };
  return <div className="platform-menu" ref={container} onClick={event => event.stopPropagation()} onKeyDown={event => {
    if (event.key === 'Escape') { setOpen(false); trigger.current?.focus(); }
    if (event.key === 'Tab') setOpen(false);
    if (open && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      const buttons = Array.from(container.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
      buttons[event.key === 'Home' ? 0 : buttons.length - 1]?.focus();
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const buttons = Array.from(container.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      buttons[(index + (event.key === 'ArrowDown' ? 1 : buttons.length - 1) + buttons.length) % buttons.length]?.focus();
    }
  }}>
    <button ref={trigger} type="button" className="platform-icon" aria-label="Platform menu" title="Platform menu" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(value => !value)}><Menu size={21} /></button>
    {open && <div className="platform-menu-items" role="menu" aria-label="Platform">
      <button role="menuitem" onClick={() => navigate('releases')}><History size={18} />Features and Fixes</button>
      <button role="menuitem" onClick={() => navigate('roadmap')}><Route size={18} />Roadmap/Upcoming features</button>
      <button role="menuitem" onClick={() => navigate('bug')}><BugIcon size={18} />Submit a bug</button>
      <button role="menuitem" onClick={() => navigate('feature-request')}><Lightbulb size={18} />Submit a Feature Request</button>
      <div role="none"><PlatformVersions admin={false} /></div>
    </div>}
  </div>;
}

export function PlatformShellContent({ children, portal, page }: { children: React.ReactNode; portal: Portal; page: string }) {
  const identity = usePlatformIdentity();
  return <PlatformShellContentInner key={JSON.stringify([identity, portal])} portal={portal} page={page}>{children}</PlatformShellContentInner>;
}

function PlatformShellContentInner({ children, portal, page }: { children: React.ReactNode; portal: Portal; page: string }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const feedbackPage = page.startsWith('/') ? page.split(/[?#]/)[0] : location.pathname;
  const view = params.get('platformView');
  const [feedbackKind, setFeedbackKind] = useState<'bug' | 'feature-request' | null>(view === 'bug' || view === 'feature-request' ? view : null);
  useEffect(() => { if (!feedbackKind && (view === 'bug' || view === 'feature-request')) setFeedbackKind(view); }, [view, feedbackKind]);
  const close = () => { const next = new URLSearchParams(params); next.delete('platformView'); setParams(next, { replace: true }); };
  return <>
    {view === 'releases' || view === 'roadmap' ? <section className="platform-surface">
      <div className="platform-toolbar"><h2>{view === 'releases' ? 'Features and Fixes' : 'Roadmap/Upcoming features'}</h2><button className="platform-icon" onClick={close} title="Return to portal" aria-label="Return to portal"><X size={20} /></button></div>
      <PlatformPages view={view} />
    </section> : children}
    {feedbackKind && <BugReportModal key={feedbackKind} kind={feedbackKind} portal={portal} page={feedbackPage} dismissRequested={view !== feedbackKind} onKeepEditing={() => {
      if (view !== feedbackKind) { const next = new URLSearchParams(params); next.set('platformView', feedbackKind); setParams(next, { replace: true }); }
    }} onClose={() => { setFeedbackKind(null); if (view === feedbackKind) close(); }} />}
  </>;
}